import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import * as topikAPI from "../../Data/Topik";
import { TopikRole, TopikUserRoleType } from "../../Data/Topik/types";
import { localStorageDB } from "@/Data/LocalStorage";

const STORAGE_KEY = 'topik_users';

type UsersState = {
    items: Record<string, TopikUserRoleType>;
    byRole: Record<TopikRole, string[]>;
    totalByRole: Record<TopikRole, number>;
    loading: boolean;
    error: string | null;
};

const validateUsersState = (state: any): state is UsersState => {
    return state &&
        typeof state.items === 'object' &&
        typeof state.byRole === 'object' &&
        typeof state.totalByRole === 'object' &&
        typeof state.loading === 'boolean';
};

// localStorage effect for persistence
const localStorageEffect = (key: string) => ({ setSelf, onSet }: { setSelf: Function; onSet: Function }) => {
    try {
        const savedValue = localStorageDB.getItem(key);
        if (savedValue) {
            const parsed = JSON.parse(savedValue);
            if (validateUsersState(parsed)) {
                // Reset loading/error on restore
                setSelf({ ...parsed, loading: false, error: null });
            }
        }
    } catch (error) {
        console.error('Error loading topik users from localStorage:', error);
    }

    onSet((newState: UsersState) => {
        try {
            // Don't persist loading/error states
            const { loading, error, ...persistState } = newState;
            localStorageDB.setItem(key, JSON.stringify({ ...persistState, loading: false, error: null }));
        } catch (error) {
            console.error('Error saving topik users to localStorage:', error);
        }
    });
};

// Users state atom
export const usersState = atom<UsersState>({
    key: "topikUsersState" + randomString(4),
    default: {
        items: {},
        byRole: { admin: [], manager: [], lecturer: [], student: [] },
        totalByRole: { admin: 0, manager: 0, lecturer: 0, student: 0 },
        loading: false,
        error: null
    },
    effects: [localStorageEffect(STORAGE_KEY)]
});

export const useRoles = () => {
    const [state, setState] = useRecoilState(usersState);
    const resetUsers = useResetRecoilState(usersState);

    // Sync users by role from API
    const syncUsersByRole = useCallback(async (params: {
        role: TopikRole;
        limit?: number;
        offset?: number;
    }): Promise<TopikUserRoleType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await topikAPI.listUsersByRole(params);

            if (result) {
                const items = Object.fromEntries(result.data.map(u => [u.email, u]));

                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    byRole: { ...prev.byRole, [params.role]: result.data.map(u => u.email) },
                    totalByRole: { ...prev.totalByRole, [params.role]: result.total },
                    loading: false
                }));

                return result.data;
            }

            setState(prev => ({ ...prev, loading: false }));
            return [];
        } catch (error) {
            console.error('Failed to sync users by role:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to load users' }));
            return [];
        }
    }, [setState]);

    // Sync single user role from API
    const syncUserRole = useCallback(async (params: {
        email?: string;
        addressid?: string;
    }): Promise<TopikUserRoleType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const user = await topikAPI.getUserRole(params);

            if (user) {
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, [user.email]: user },
                    loading: false
                }));

                return user;
            }

            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to sync user role:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to get user role' }));
            return null;
        }
    }, [setState]);

    // Assign user role (sync to API and update state)
    const assignUserRole = useCallback(async (params: {
        email?: string;
        addressid?: string;
        role: TopikRole;
    }): Promise<TopikUserRoleType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const user = await topikAPI.assignUserRole(params);

            if (user) {
                setState(prev => {
                    // Remove user from previous role list
                    const newByRole = { ...prev.byRole };
                    for (const role of Object.keys(newByRole) as TopikRole[]) {
                        newByRole[role] = newByRole[role].filter(email => email !== user.email);
                    }
                    // Add to new role list
                    if (!newByRole[user.role].includes(user.email)) {
                        newByRole[user.role] = [...newByRole[user.role], user.email];
                    }

                    return {
                        ...prev,
                        items: { ...prev.items, [user.email]: user },
                        byRole: newByRole,
                        loading: false
                    };
                });

                return user;
            }

            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to assign user role:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to assign user role' }));
            return null;
        }
    }, [setState]);

    // Remove user role (sync to API and update state)
    const removeUserRole = useCallback(async (params: {
        email?: string;
        addressid?: string;
    }): Promise<boolean> => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const success = await topikAPI.removeUserRole(params);

            if (success) {
                const email = params.email;
                setState(prev => {
                    const newItems = { ...prev.items };
                    const newByRole = { ...prev.byRole };

                    if (email) {
                        delete newItems[email];
                        for (const role of Object.keys(newByRole) as TopikRole[]) {
                            newByRole[role] = newByRole[role].filter(e => e !== email);
                        }
                    }

                    return { ...prev, items: newItems, byRole: newByRole, loading: false };
                });

                return true;
            }

            setState(prev => ({ ...prev, loading: false }));
            return false;
        } catch (error) {
            console.error('Failed to remove user role:', error);
            setState(prev => ({ ...prev, loading: false, error: 'Failed to remove user role' }));
            return false;
        }
    }, [setState]);

    // Get user from state (read-only, no API call)
    const getUser = useCallback((email: string): TopikUserRoleType | null => {
        return state.items[email] ?? null;
    }, [state.items]);

    // Get users by role from state (read-only, no API call)
    const getUsersByRole = useCallback((role: TopikRole): TopikUserRoleType[] => {
        return state.byRole[role].map(email => state.items[email]).filter(Boolean);
    }, [state.items, state.byRole]);

    const reset = useCallback(() => {
        resetUsers();
    }, [resetUsers]);

    return {
        roles: state.items,
        byRole: state.byRole,
        totalByRole: state.totalByRole,
        loading: state.loading,
        error: state.error,
        syncUsersByRole,
        syncUserRole,
        assignUserRole,
        removeUserRole,
        getUser,
        getUsersByRole,
        reset
    };
};
